/**
 * RequestResearchModal — the owner-initiated half of the Mysteries
 * marketplace (Layer 3). Lets a tree owner author a quest from scratch (no
 * Origin AI run required), which lands as a pending suggestion in their
 * Suggestions Inbox where they can Promote it or attach a bounty.
 *
 * Three quest kinds:
 *   investigate_gap — find missing info (parents, dates, a whole branch)
 *   confirm_fact    — verify an existing field with a citation
 *   find_photo      — locate a portrait of this person
 * confirm_fact + find_photo are person-scoped; investigate_gap can be
 * tree-level. Submits to POST /api/mysteries/requests.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../services/api.js'

const GAP_TYPES = [
  { value: 'vertical',   label: 'Vertical', hint: 'Missing ancestors — parents, grandparents, an unknown generation further back.' },
  { value: 'horizontal', label: 'Horizontal', hint: 'Missing relatives in the same generation — siblings, a spouse, cousins.' },
  { value: 'whitespot',  label: 'Whitespot', hint: 'Someone who likely existed (naming pattern, place + era cohort) but isn’t in the tree yet.' },
]

const CONFIRM_FIELDS = [
  ['birth_date', 'Birth date'], ['birth_place', 'Birth place'],
  ['death_date', 'Death date'], ['death_place', 'Death place'],
  ['first_name', 'First name'], ['last_name', 'Last name'],
  ['birth_name', 'Birth name'], ['occupation', 'Occupation'],
]

export default function RequestResearchModal({ treeId, personId, personName, onClose }) {
  const navigate = useNavigate()
  const hasPerson = !!personId

  const [kind, setKind] = useState('investigate_gap')
  const [title, setTitle] = useState(personName ? `Help with ${personName}` : '')
  const [description, setDescription] = useState('')
  const [likelyEra, setLikelyEra] = useState('')
  const [likelyPlace, setLikelyPlace] = useState('')
  const [gapType, setGapType] = useState('vertical')
  const [field, setField] = useState('birth_date')
  const [posting, setPosting] = useState(false)
  const [err, setErr] = useState(null)
  const [done, setDone] = useState(false)

  const KINDS = [
    { value: 'investigate_gap', label: 'Find missing info', needsPerson: false },
    { value: 'confirm_fact',    label: 'Verify a fact',     needsPerson: true },
    { value: 'find_photo',      label: 'Find a photo',      needsPerson: true },
  ]

  async function submit(e) {
    e.preventDefault()
    if (!title.trim() || !description.trim()) {
      setErr('A title and a description are required.')
      return
    }
    setPosting(true)
    setErr(null)
    try {
      await api.post('/mysteries/requests', {
        tree_id: treeId,
        person_id: personId || null,
        kind,
        title: title.trim(),
        description: description.trim(),
        likely_era: kind === 'investigate_gap' ? (likelyEra.trim() || null) : null,
        likely_place: kind === 'investigate_gap' ? (likelyPlace.trim() || null) : null,
        gap_type: kind === 'investigate_gap' ? gapType : null,
        field: kind === 'confirm_fact' ? field : null,
      })
      setDone(true)
    } catch (e) {
      setErr(e.message || 'Could not create the request')
      setPosting(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal request-research-modal" onClick={e => e.stopPropagation()}>
        <header className="modal__header">
          <h2>{done ? 'Request created' : 'Request research help'}</h2>
          <button className="modal__close" onClick={onClose} aria-label="Close">×</button>
        </header>

        {done ? (
          <div className="request-research-modal__body">
            <p className="request-research-modal__intro">
              Your request is now a lead in your Suggestions Inbox. From there
              you can <strong>Promote</strong> it to the marketplace for free,
              or attach a <strong>cash bounty</strong> to attract solvers faster.
            </p>
            <div className="request-research-modal__actions">
              <button type="button" className="btn btn-ghost" onClick={onClose}>Close</button>
              <button type="button" className="btn btn-primary" onClick={() => navigate('/suggestions')}>
                Go to inbox →
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="request-research-modal__body">
            <p className="request-research-modal__intro">
              Stuck on a gap? Post it as a mystery — other researchers can pick
              it up. No AI run required.
            </p>

            <div className="request-research-modal__field">
              <span>What kind of help?</span>
              <div className="request-research-modal__gaptypes">
                {KINDS.map(k => {
                  const disabled = k.needsPerson && !hasPerson
                  return (
                    <button
                      key={k.value} type="button"
                      className={`request-research-modal__gaptype ${kind === k.value ? 'is-active' : ''}`}
                      onClick={() => setKind(k.value)}
                      disabled={disabled}
                      title={disabled ? 'Open this from a specific person to use this option' : ''}
                    >{k.label}</button>
                  )
                })}
              </div>
            </div>

            <label className="request-research-modal__field">
              <span>Title</span>
              <input
                type="text" value={title} maxLength={120}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g. Find Pieter’s parents"
              />
            </label>

            <label className="request-research-modal__field">
              <span>{kind === 'find_photo' ? 'Who are we looking for, and what do you know?' : 'What do you need?'}</span>
              <textarea
                value={description} rows={4} maxLength={2000}
                onChange={e => setDescription(e.target.value)}
                placeholder="What’s known, what’s missing, and where a solver might look."
              />
            </label>

            {kind === 'confirm_fact' && (
              <label className="request-research-modal__field">
                <span>Which fact should be verified?</span>
                <select value={field} onChange={e => setField(e.target.value)}>
                  {CONFIRM_FIELDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
            )}

            {kind === 'investigate_gap' && (
              <>
                <div className="request-research-modal__row">
                  <label className="request-research-modal__field">
                    <span>Likely era <em>(optional)</em></span>
                    <input
                      type="text" value={likelyEra} maxLength={60}
                      onChange={e => setLikelyEra(e.target.value)}
                      placeholder="e.g. ~1875"
                    />
                  </label>
                  <label className="request-research-modal__field">
                    <span>Likely place <em>(optional)</em></span>
                    <input
                      type="text" value={likelyPlace} maxLength={120}
                      onChange={e => setLikelyPlace(e.target.value)}
                      placeholder="e.g. Rotterdam"
                    />
                  </label>
                </div>

                <div className="request-research-modal__field">
                  <span>Type of gap</span>
                  <div className="request-research-modal__gaptypes">
                    {GAP_TYPES.map(g => (
                      <button
                        key={g.value} type="button"
                        className={`request-research-modal__gaptype ${gapType === g.value ? 'is-active' : ''}`}
                        onClick={() => setGapType(g.value)}
                        title={g.hint}
                      >{g.label}</button>
                    ))}
                  </div>
                  <p className="request-research-modal__gaphint">
                    {GAP_TYPES.find(g => g.value === gapType)?.hint}
                  </p>
                </div>
              </>
            )}

            {err && <div className="request-research-modal__err">{err}</div>}

            <div className="request-research-modal__actions">
              <button type="button" className="btn btn-ghost" onClick={onClose} disabled={posting}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={posting}>
                {posting ? 'Creating…' : 'Create request →'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
